/**
 * E6B explanation sequencer (ported from public/flight-computer/e6b-player/player.js).
 * Drives a wheel host (Angular FlightComputer) without owning the SVG DOM.
 */

import {
  CALCULATOR_INNER_INDEX_R,
  CALCULATOR_INNER_NUMERAL_R,
  CALCULATOR_OUTER_INDEX_R,
  CALCULATOR_OUTER_NUMERAL_R,
  CALCULATOR_UNITS,
  calculatorAngleOfValue as scaleAngleOfValue,
  calculatorMantissa as scaleMantissa,
  calculatorPointAtAngle as scalePointAtAngle,
  calculatorResolveUnit as scaleResolveUnit,
  calculatorUnitName as scaleUnitName,
  calculatorUnitLabelSpot as scaleUnitLabelSpot,
  calculatorUnitHighlight as scaleUnitHighlight,
  calculatorUnitValue,
  calculatorRotationToOpposeUnits as scaleRotationToOpposeUnits,
  calculatorRotationToOpposeValues as scaleRotationToOpposeValues,
  calculatorPressureAltAngle as scalePressureAltAngle,
  CALCULATOR_MACH_INDEX,
  CALCULATOR_PA_R,
} from './e6b-calculator-scale';

export type E6bMode = 'calculator' | 'wind|low' | 'wind|high';

export type PlaybackMark =
  | { kind: 'dot'; x: number; y: number; label?: string; ring?: 'inner' | 'outer' }
  | {
      kind: 'line';
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      ring?: 'inner' | 'outer';
    };

export interface E6bOp {
  op?: string;
  mode?: string;
  rotation?: number;
  to?: number;
  deg?: number;
  by?: number;
  slideY?: number;
  tas_kt?: number;
  dir?: string;
  x?: number;
  y?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  from?: string;
  offset_kt?: number;
  drift_deg?: number;
  dir_deg?: number;
  heading?: number;
  /**
   * Calculator conversion: turn until OUTER unit `from` sits against INNER unit `to`
   * (e.g. outer US GAL against inner LITERS), then read values across the scales.
   */
  oppose?: { from?: string; to?: string; from_value?: number; to_value?: number };
  /** Legacy: put a dial value under one outer conversion mark. */
  align?: { value?: number; mark?: string; mark_deg?: number };
  value?: number;
  radius?: number;
  ring?: 'inner' | 'outer';
  /** Unit index name for `from: "unit"` dots (us_gal, liters, …). */
  mark?: string;
  label?: string;
  text?: string;
  targets?: unknown;
  target?: unknown;
  [key: string]: unknown;
}

const DEFAULT_DELAY_MS = 2200;
const TURN_MS = 1800;
const SLIDE_MS = 1400;

const ASSET: Record<string, true> = {
  calculator: true,
  'wind|low': true,
  'wind|high': true,
};

const INTERNAL_OPS = new Set(['set_mode', 'clear_marks']);

const MECHANIC_OPS = new Set([
  'turn_left',
  'turn_right',
  'move_slider',
  'draw_dot',
  'draw_line',
  'highlight',
]);

export function captionHasMechanic(instructions: E6bOp[], captionIndex: number): boolean {
  for (let i = captionIndex + 1; i < instructions.length; i++) {
    const name = String(instructions[i]?.op || '');
    if (name === 'caption') return false;
    if (MECHANIC_OPS.has(name)) return true;
  }
  return false;
}

/** Counted step: a caption that is followed by a move. Intro / readout captions are not steps. */
export function isUserStep(
  op: E6bOp | null | undefined,
  index?: number,
  instructions?: E6bOp[],
): boolean {
  if (!op || String(op.op || '') !== 'caption') return false;
  if (!instructions || index == null) return false;
  return captionHasMechanic(instructions, index);
}

function isInternal(op: E6bOp | null | undefined): boolean {
  return !!(op && INTERNAL_OPS.has(String(op.op || '')));
}

/** Static calibration helpers; class form avoids TS7022 circular const inference. */
export class E6bCalibrator {
  static readonly windHigh = { tas0: 835, pxPerKt: 0.711 };
  static readonly windGrommetY = 500.35;
  /** Low-card centreline speed arcs on wind-low.svg (not the printed numerals). */
  static readonly windLowCardY: ReadonlyArray<readonly [number, number]> = [
    [40, 954.17],
    [50, 918.42],
    [60, 882.67],
    [70, 846.92],
    [80, 811.16],
    [90, 775.41],
    [100, 739.66],
    [110, 703.91],
    [120, 668.16],
    [130, 632.41],
    [140, 596.66],
    [150, 560.91],
    [160, 525.16],
    [170, 489.40],
    [180, 453.65],
    [190, 417.90],
    [200, 382.15],
    [210, 346.40],
    [220, 310.65],
    [230, 274.90],
    [240, 239.15],
    [250, 203.40],
    [260, 167.64],
  ];
  static readonly windLow = { tas0: 167.38, pxPerKt: 3.56 };
  /**
   * Conversion indexes as scale values (CRP-5). Angles come from the
   * measured tick table in e6b-calculator-scale.ts (10 at 12 o'clock).
   */
  static readonly calculatorMarkScale: Record<string, number> = Object.fromEntries(
    Object.entries(CALCULATOR_UNITS).map(([k, u]) => [k, u.value]),
  );
  static readonly calculatorOuterIndexRadius = CALCULATOR_OUTER_INDEX_R;
  static readonly calculatorInnerIndexRadius = CALCULATOR_INNER_INDEX_R;
  static readonly calculatorScaleRadius = CALCULATOR_INNER_NUMERAL_R;
  static readonly calculatorOuterScaleRadius = CALCULATOR_OUTER_NUMERAL_R;
  static readonly calculatorMarkRadius = CALCULATOR_OUTER_INDEX_R;
  static readonly calculatorInnerMarkRadius = CALCULATOR_INNER_INDEX_R;
  static readonly calculatorUnitNames: Record<string, string> = Object.fromEntries(
    Object.entries(CALCULATOR_UNITS).map(([k, u]) => [k, u.name]),
  );
  static readonly trueIndexHighlight = { x: 330.07, y: 270, r: 34 };
  static readonly machIndexHighlight = CALCULATOR_MACH_INDEX;

  static windRotationForHeading(headingDeg: number): number {
    const h = ((Number(headingDeg) % 360) + 360) % 360;
    return -h;
  }

  static _interpTable(
    x: number,
    table: ReadonlyArray<readonly [number, number]>,
    xy: 'xy' | 'yx',
  ): number {
    if (!table.length) return 0;
    const a = xy === 'yx' ? 1 : 0;
    const b = xy === 'yx' ? 0 : 1;
    const first = table[0][a];
    const last = table[table.length - 1][a];
    const rising = last >= first;
    if (rising) {
      if (x <= first) return table[0][b];
      if (x >= last) return table[table.length - 1][b];
    } else {
      if (x >= first) return table[0][b];
      if (x <= last) return table[table.length - 1][b];
    }
    for (let i = 1; i < table.length; i++) {
      const x0 = table[i - 1][a];
      const x1 = table[i][a];
      const lo = Math.min(x0, x1);
      const hi = Math.max(x0, x1);
      if (x >= lo && x <= hi) {
        const t = (x - x0) / (x1 - x0);
        return table[i - 1][b] + t * (table[i][b] - table[i - 1][b]);
      }
    }
    return table[table.length - 1][b];
  }

  /**
   * Carry the low card's scale on past the printed grid instead of stopping at
   * it. The table used to clamp, so a mark belonging above the card was drawn
   * on the card's top edge: q9423 asked for 290 kt on a card ending at 260 and
   * got a wind vector 60% short of the length its own caption claimed, with
   * nothing on screen to say so. Running off the grid should look like it.
   */
  private static _lowCard(v: number, xy: 'xy' | 'yx'): number {
    const pts = E6bCalibrator.windLowCardY;
    const a = xy === 'yx' ? 1 : 0;
    const b = 1 - a;
    const lo = pts[0];
    const hi = pts[pts.length - 1];
    const carry = (end: readonly number[], next: readonly number[]) =>
      end[b] + ((v - end[a]) * (next[b] - end[b])) / (next[a] - end[a]);
    // Speed rises up the table while Y falls, so which end is "past" depends on
    // which column was handed in.
    if (a === 0 ? v < lo[a] : v > lo[a]) return carry(lo, pts[1]);
    if (a === 0 ? v > hi[a] : v < hi[a]) return carry(hi, pts[pts.length - 2]);
    return E6bCalibrator._interpTable(v, pts, xy);
  }

  static cardYAtTas(tas: number, scale: 'low' | 'high'): number {
    if (scale === 'low') return E6bCalibrator._lowCard(tas, 'xy');
    const cfg = E6bCalibrator.windHigh;
    return E6bCalibrator.windGrommetY - (tas - cfg.tas0) * cfg.pxPerKt;
  }

  static tasAtCardY(y: number, scale: 'low' | 'high'): number {
    if (scale === 'low') return E6bCalibrator._lowCard(y, 'yx');
    const cfg = E6bCalibrator.windHigh;
    return cfg.tas0 + (E6bCalibrator.windGrommetY - y) / cfg.pxPerKt;
  }

  /** Slowest speed each card prints a grid for. */
  static readonly windFloorKt: Record<'low' | 'high', number> = { low: 40, high: 200 };

  static windSlideForTas(tas: number, scale: 'low' | 'high'): number {
    const t = Number(tas);
    if (!Number.isFinite(t)) return 0;
    // Stop the card where its own grid stops, not at a round number. A flat
    // 420 px held the high card at 244 kt, well inside artwork that runs down to
    // 200, so a walkthrough asking for less did not fail: the card stopped
    // moving and the rest of it played against a blank instrument.
    const floor =
      E6bCalibrator.windGrommetY -
      E6bCalibrator.cardYAtTas(E6bCalibrator.windFloorKt[scale], scale);
    return Math.max(
      floor,
      Math.min(420, E6bCalibrator.windGrommetY - E6bCalibrator.cardYAtTas(t, scale)),
    );
  }

  /** Radius of the speed arc for `tas`, measured from the centre the arcs share. */
  static windArcRadius(tas: number, scale: 'low' | 'high'): number {
    const cfg = scale === 'low' ? E6bCalibrator.windLow : E6bCalibrator.windHigh;
    const centreY = E6bCalibrator.windGrommetY + cfg.tas0 * cfg.pxPerKt;
    return centreY - E6bCalibrator.cardYAtTas(tas, scale);
  }

  static windDotFromGrommet(
    offsetKt: number,
    driftDeg: number,
    cx: number,
    cy: number,
    scale: 'low' | 'high',
    rotationDeg: number,
    slideY?: number,
  ): { x: number; y: number } {
    const kt = Number(offsetKt) || 0;
    const drift = Number(driftDeg) || 0;
    const cfg = scale === 'low' ? E6bCalibrator.windLow : E6bCalibrator.windHigh;
    const tasNow = Number.isFinite(slideY)
      ? E6bCalibrator.tasAtCardY(E6bCalibrator.windGrommetY - Number(slideY), scale)
      : cfg.tas0;
    // The drift lines fan out from the centre the speed arcs share, so one degree
    // is worth r * pi/180 px and widens with speed. Place the mark on its own
    // speed arc at the drift bearing rather than a fixed distance sideways.
    const rGrommet = E6bCalibrator.windArcRadius(tasNow, scale);
    const rMark = E6bCalibrator.windArcRadius(tasNow + kt, scale);
    const d = (drift * Math.PI) / 180;
    const screenDx = rMark * Math.sin(d);
    const screenDy = rGrommet - rMark * Math.cos(d);
    const θ = ((Number(rotationDeg) || 0) * Math.PI) / 180;
    const cos = Math.cos(θ);
    const sin = Math.sin(θ);
    const localDx = screenDx * cos + screenDy * sin;
    const localDy = -screenDx * sin + screenDy * cos;
    return { x: cx + localDx, y: cy + localDy };
  }

  static calculatorMantissa(v: number): number {
    return scaleMantissa(v);
  }

  /** Dial-local angle of a scale value (measured ticks, 10 at 12 o'clock). */
  static calculatorAngleOfValue(v: number): number {
    return scaleAngleOfValue(v);
  }

  static calculatorResolveUnit(mark: string | number | undefined): string {
    return scaleResolveUnit(mark);
  }

  static calculatorUnitName(mark: string | number | undefined): string {
    return scaleUnitName(mark);
  }

  static calculatorMarkAngle(mark: string | number | undefined): number | null {
    if (typeof mark === 'number' && Number.isFinite(mark)) {
      return ((mark % 360) + 360) % 360;
    }
    const scaleVal = calculatorUnitValue(mark);
    if (scaleVal == null) return null;
    return scaleAngleOfValue(scaleVal);
  }

  /**
   * Rotation that opposes OUTER unit `fromUnit` with INNER unit `toUnit`
   * (matches CRP-5 teaching figures: e.g. outer US GAL against inner LITERS).
   * Then find the source on the from-unit side (outer) and read the to-unit side (inner).
   */
  static calculatorRotationToOpposeUnits(
    fromUnit: string | number,
    toUnit: string | number,
  ): number {
    return scaleRotationToOpposeUnits(String(fromUnit), String(toUnit));
  }

  static calculatorRotationToOpposeValues(outerValue: number, innerValue: number): number {
    return scaleRotationToOpposeValues(outerValue, innerValue);
  }

  static calculatorPressureAltSpot(
    paThousands: number,
    cx: number,
    cy: number,
  ): { x: number; y: number; r: number } | null {
    const ang = scalePressureAltAngle(paThousands);
    if (ang == null) return null;
    const pt = scalePointAtAngle(ang, cx, cy, CALCULATOR_PA_R);
    return { x: pt.x, y: pt.y, r: 16 };
  }

  /**
   * Legacy: place dial value `value` under outer mark `mark`.
   */
  static calculatorRotationToAlign(value: number, mark: string | number): number {
    const markAng = E6bCalibrator.calculatorMarkAngle(mark);
    if (markAng == null || !Number.isFinite(value)) return 0;
    let r = markAng - E6bCalibrator.calculatorAngleOfValue(value);
    r = ((r % 360) + 360) % 360;
    if (r > 180) r -= 360;
    return r;
  }

  static calculatorPointAtAngle(
    angleDeg: number,
    cx: number,
    cy: number,
    radius: number,
  ): { x: number; y: number } {
    return scalePointAtAngle(angleDeg, cx, cy, radius);
  }

  /** Unit index on the measured tick (13.1 / 49.6 / …). */
  static calculatorLabelSpot(
    mark: string,
    ring: 'inner' | 'outer',
  ): { ang: number; r: number } | null {
    const ang = E6bCalibrator.calculatorMarkAngle(mark);
    if (ang == null) return null;
    return {
      ang,
      r:
        ring === 'outer'
          ? E6bCalibrator.calculatorOuterIndexRadius
          : E6bCalibrator.calculatorInnerIndexRadius,
    };
  }

  /** Printed unit caption (Mini_Text / Small_Outer_Text). */
  static calculatorUnitCaptionSpot(
    mark: string,
    ring: 'inner' | 'outer',
  ): { ang: number; r: number } | null {
    return scaleUnitLabelSpot(mark, ring);
  }

  /** Circle covering the printed unit word and its blue arrow. */
  static calculatorUnitHighlight(
    mark: string,
    ring: 'inner' | 'outer',
    cx: number,
    cy: number,
  ): { x: number; y: number; r: number } | null {
    return scaleUnitHighlight(mark, ring, cx, cy);
  }

  static calculatorPointAtMark(
    mark: string | number,
    cx: number,
    cy: number,
    radius?: number,
  ): { x: number; y: number } | null {
    const ang = E6bCalibrator.calculatorMarkAngle(mark);
    if (ang == null) return null;
    return E6bCalibrator.calculatorPointAtAngle(
      ang,
      cx,
      cy,
      Number.isFinite(radius as number)
        ? (radius as number)
        : E6bCalibrator.calculatorOuterIndexRadius,
    );
  }

  static calculatorPointAtValue(
    value: number,
    cx: number,
    cy: number,
    radius = E6bCalibrator.calculatorScaleRadius,
  ): { x: number; y: number } {
    return E6bCalibrator.calculatorPointAtAngle(
      E6bCalibrator.calculatorAngleOfValue(value),
      cx,
      cy,
      radius,
    );
  }
}

export interface E6bWheelDriver {
  readonly rotation: number;
  readonly slideY: number;
  readonly wheelCx: number;
  readonly wheelCy: number;
  readonly mode: E6bMode;
  ensureMode(mode: E6bMode): Promise<void>;
  setRotation(deg: number, opts?: { instant?: boolean; speed?: number }): Promise<void>;
  setSlideY(y: number, opts?: { instant?: boolean; speed?: number }): Promise<void>;
  getMarks(): PlaybackMark[];
  setMarks(marks: PlaybackMark[]): void;
  clearMarks(): void;
  setCaption(text: string): void;
  setHighlights(targets: unknown): void;
  clearOverlays(): void;
  cancelAnimations(): void;
  /** Suppress DOM paints while rebuilding; end flushes once. */
  beginSilentUpdate(): void;
  endSilentUpdate(): void;
}

export interface E6bStepInfo {
  index: number;
  total: number;
  userIndex: number;
  userTotal: number;
  op: E6bOp | null;
  internal: boolean;
}

export class E6bPlaybackController {
  instructions: E6bOp[] = [];
  index = -1;
  playing = false;
  delayMs = DEFAULT_DELAY_MS;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private abort = false;
  private onStep: (info: E6bStepInfo) => void;
  /** 1 = normal; 2 = button stepping (prev/next). */
  private animSpeed = 1;

  constructor(
    private wheel: E6bWheelDriver,
    opts: { delayMs?: number; onStep?: (info: E6bStepInfo) => void } = {},
  ) {
    this.delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;
    this.onStep = opts.onStep || (() => {});
  }

  async loadInstructions(list: unknown[]): Promise<void> {
    this.instructions = Array.isArray(list) ? (list as E6bOp[]).slice() : [];
    this.index = -1;
    this.playing = false;
    this.clearTimer();
    this.wheel.cancelAnimations();
    await this.wheel.ensureMode(this.modeFromInstructions());
    this.wheel.clearOverlays();
    this.emitStep(null);
  }

  async play(): Promise<void> {
    if (!this.instructions.length) return;
    this.playing = true;
    this.abort = false;
    if (this.index < 0 || this.index >= this.instructions.length - 1) {
      this.index = -1;
      await this.wheel.ensureMode(this.modeFromInstructions());
      await this.wheel.setRotation(0, { instant: true });
      await this.wheel.setSlideY(0, { instant: true });
      this.wheel.clearMarks();
      this.wheel.clearOverlays();
    }
    await this.runFromHere();
  }

  pause(): void {
    this.playing = false;
    this.clearTimer();
    this.wheel.cancelAnimations();
  }

  async replay(): Promise<void> {
    this.pause();
    this.index = -1;
    await this.play();
  }

  async next(): Promise<void> {
    this.pause();
    const cap = this.findUserStepIndex(this.index + 1, 1);
    const end = cap < 0 ? this.instructions.length - 1 : this.beatEndIndex(cap);
    if (end < 0 || this.index >= end) return;
    this.playing = true;
    this.abort = false;
    this.animSpeed = 2;
    try {
      while (this.playing && !this.abort && this.index < end) {
        await this.stepOnce();
      }
    } finally {
      this.animSpeed = 1;
      this.playing = false;
    }
  }

  async prev(): Promise<void> {
    this.pause();
    const currentCap = this.currentUserStepCaption();
    const target = this.findUserStepIndex(currentCap - 1, -1);
    if (target < 0) {
      this.index = -1;
      this.wheel.beginSilentUpdate();
      try {
        await this.wheel.ensureMode(this.wheel.mode);
        await this.wheel.setRotation(0, { instant: true });
        await this.wheel.setSlideY(0, { instant: true });
        this.wheel.clearMarks();
        this.wheel.clearOverlays();
      } finally {
        this.wheel.endSilentUpdate();
      }
      this.emitStep(null);
      return;
    }
    await this.rebuildTo(this.beatEndIndex(target));
  }

  private isUserStepAt(i: number): boolean {
    return captionHasMechanic(this.instructions, i) &&
      String(this.instructions[i]?.op || '') === 'caption';
  }

  /** Walk from `start` in `dir` (+1/-1) to the next counted move-caption. */
  private findUserStepIndex(start: number, dir: 1 | -1): number {
    if (dir > 0) {
      for (let i = start; i < this.instructions.length; i++) {
        if (this.isUserStepAt(i)) return i;
      }
      return -1;
    }
    for (let i = start; i >= 0; i--) {
      if (this.isUserStepAt(i)) return i;
    }
    return -1;
  }

  private currentUserStepCaption(): number {
    for (let i = this.index; i >= 0; i--) {
      if (this.isUserStepAt(i)) return i;
    }
    return -1;
  }

  private beatEndIndex(captionIndex: number): number {
    const list = this.instructions;
    if (captionIndex < 0) return -1;
    let end = captionIndex;
    while (end + 1 < list.length && String(list[end + 1]?.op || '') !== 'caption') {
      end += 1;
    }
    let next = end + 1;
    while (
      next < list.length &&
      String(list[next]?.op || '') === 'caption' &&
      !this.isUserStepAt(next)
    ) {
      end = next;
      next += 1;
      while (next < list.length && String(list[next]?.op || '') !== 'caption') {
        end = next;
        next += 1;
      }
    }
    return end;
  }

  destroy(): void {
    this.pause();
    this.abort = true;
  }

  userFacingCount(): number {
    let n = 0;
    for (let i = 0; i < this.instructions.length; i++) {
      if (this.isUserStepAt(i)) n += 1;
    }
    return n;
  }

  userFacingIndex(absIndex: number): number {
    if (absIndex < 0) return 0;
    let n = 0;
    for (let i = 0; i <= absIndex && i < this.instructions.length; i++) {
      if (this.isUserStepAt(i)) n += 1;
    }
    return n;
  }

  private emitStep(op: E6bOp | null): void {
    this.onStep({
      index: this.index,
      total: this.instructions.length,
      userIndex: this.userFacingIndex(this.index),
      userTotal: this.userFacingCount(),
      op,
      internal: isInternal(op),
    });
  }

  private async runFromHere(): Promise<void> {
    while (this.playing && !this.abort && this.index < this.instructions.length - 1) {
      await this.stepOnce();
      if (!this.playing || this.abort) break;
      const just = this.instructions[this.index];
      if (just && String(just.op || '') === 'caption') {
        const end = this.beatEndIndex(this.index);
        while (this.playing && !this.abort && this.index < end) {
          await this.stepOnce();
        }
        if (!this.playing || this.abort) break;
        await this.sleep(this.delayMs);
      }
    }
    this.playing = false;
  }

  private async stepOnce(): Promise<void> {
    const next = this.index + 1;
    if (next >= this.instructions.length) return;
    this.index = next;
    const op = this.instructions[this.index];
    await this.applyOp(op);
    this.emitStep(op);
  }

  private async rebuildTo(targetIndex: number): Promise<void> {
    this.pause();
    this.abort = false;
    // Rebuild off-screen so the card doesn't flash slide/rotation = 0 mid-way.
    this.wheel.beginSilentUpdate();
    try {
      this.wheel.clearOverlays();
      await this.wheel.ensureMode(this.modeFromInstructions(targetIndex));
      await this.wheel.setRotation(0, { instant: true });
      await this.wheel.setSlideY(0, { instant: true });
      this.wheel.clearMarks();
      this.index = -1;
      for (let i = 0; i <= targetIndex; i++) {
        this.index = i;
        await this.applyOp(this.instructions[i], { instant: true });
      }
    } finally {
      this.wheel.endSilentUpdate();
    }
    this.emitStep(this.instructions[this.index] || null);
  }

  private modeFromInstructions(upto?: number): E6bMode {
    const last = Number.isFinite(upto) ? (upto as number) : this.instructions.length - 1;
    let mode: E6bMode =
      this.wheel.mode && ASSET[this.wheel.mode] ? this.wheel.mode : 'wind|low';
    for (let i = 0; i <= last && i < this.instructions.length; i++) {
      const op = this.instructions[i];
      if (op && op.op === 'set_mode' && ASSET[String(op.mode || '')]) {
        mode = String(op.mode) as E6bMode;
      }
    }
    return mode;
  }

  private async applyOp(op: E6bOp, opts: { instant?: boolean } = {}): Promise<void> {
    if (!op || typeof op !== 'object') return;
    const instant = !!opts.instant;
    const name = String(op.op || '');
    switch (name) {
      case 'set_mode':
        await this.wheel.ensureMode(String(op.mode || 'wind|low') as E6bMode);
        break;
      case 'turn_left':
      case 'turn_right':
        await this.applyTurn(op, name === 'turn_right' ? 1 : -1, instant);
        break;
      case 'move_slider':
        await this.applySlider(op, instant);
        break;
      case 'draw_dot':
        this.applyDot(op);
        break;
      case 'draw_line':
        this.applyLine(op);
        break;
      case 'clear_marks':
        this.wheel.clearMarks();
        this.wheel.setHighlights([]);
        break;
      case 'caption':
        this.wheel.setCaption(String(op.text || ''));
        break;
      case 'highlight':
        this.wheel.setHighlights(op.targets || op.target || []);
        break;
      default:
        break;
    }
  }

  private targetRotation(op: E6bOp, dirSign: number): number {
    const oppose = op.oppose as {
      from?: string;
      to?: string;
      from_value?: number;
      to_value?: number;
    } | undefined;
    if (
      oppose &&
      Number.isFinite(oppose.from_value as number) &&
      Number.isFinite(oppose.to_value as number)
    ) {
      return E6bCalibrator.calculatorRotationToOpposeValues(
        Number(oppose.from_value),
        Number(oppose.to_value),
      );
    }
    if (oppose?.from != null && oppose?.to != null) {
      return E6bCalibrator.calculatorRotationToOpposeUnits(oppose.from, oppose.to);
    }
    if (Number.isFinite(op.rotation as number)) return Number(op.rotation);
    const align = op.align;
    if (align && (align.value != null || op.value != null)) {
      const value = Number(align.value ?? op.value);
      const mark =
        align.mark_deg != null
          ? Number(align.mark_deg)
          : (align.mark as string | number);
      if (Number.isFinite(value) && mark != null && mark !== '') {
        return E6bCalibrator.calculatorRotationToAlign(value, mark);
      }
    }
    if (Number.isFinite(op.to as number) || Number.isFinite(op.deg as number)) {
      const to = Number.isFinite(op.to as number) ? Number(op.to) : Number(op.deg);
      if (String(this.wheel.mode).startsWith('wind')) {
        return E6bCalibrator.windRotationForHeading(to);
      }
      return -to;
    }
    if (Number.isFinite(op.by as number)) {
      return (this.wheel.rotation + dirSign * Number(op.by)) % 360;
    }
    return this.wheel.rotation;
  }

  private async applyTurn(op: E6bOp, dirSign: number, instant: boolean): Promise<void> {
    const target = this.targetRotation(op, dirSign);
    await this.wheel.setRotation(target, { instant, speed: this.animSpeed });
  }

  private targetSlide(op: E6bOp): number {
    if (!String(this.wheel.mode).startsWith('wind')) return this.wheel.slideY;
    if (Number.isFinite(op.slideY as number)) return Number(op.slideY);
    if (Number.isFinite(op.tas_kt as number)) {
      const scale = this.wheel.mode.endsWith('high') ? 'high' : 'low';
      return E6bCalibrator.windSlideForTas(Number(op.tas_kt), scale);
    }
    if (op.dir === 'left' || op.dir === 'up') {
      return Math.max(-420, this.wheel.slideY - 24);
    }
    if (op.dir === 'right' || op.dir === 'down') {
      return Math.min(420, this.wheel.slideY + 24);
    }
    return this.wheel.slideY;
  }

  private async applySlider(op: E6bOp, instant: boolean): Promise<void> {
    if (!String(this.wheel.mode).startsWith('wind')) return;
    await this.wheel.setSlideY(this.targetSlide(op), { instant, speed: this.animSpeed });
  }

  private applyDot(op: E6bOp): void {
    const mode = String(this.wheel.mode);
    const ring = op.ring === 'outer' ? 'outer' : 'inner';
    let x = Number(op.x);
    let y = Number(op.y);

    // Calculator conversions: place on the log scale. Never use the wind grommet
    // fallback here — that parks a red dot on the dial centre.
    if (mode === 'calculator') {
      if (op.from === 'unit' || op.from === 'index') {
        const spot = E6bCalibrator.calculatorLabelSpot(String(op.mark || ''), ring);
        if (!spot) return;
        const pt = E6bCalibrator.calculatorPointAtAngle(
          spot.ang,
          this.wheel.wheelCx,
          this.wheel.wheelCy,
          Number.isFinite(op.radius as number) ? Number(op.radius) : spot.r,
        );
        x = pt.x;
        y = pt.y;
      } else {
        const value = Number(op.value);
        if (
          op.from === 'scale' ||
          op.from === 'calculator_value' ||
          Number.isFinite(value)
        ) {
          if (!Number.isFinite(value)) return;
          const radius = Number.isFinite(op.radius as number)
            ? Number(op.radius)
            : ring === 'outer'
              ? E6bCalibrator.calculatorOuterScaleRadius
              : E6bCalibrator.calculatorScaleRadius;
          const pt = E6bCalibrator.calculatorPointAtValue(
            value,
            this.wheel.wheelCx,
            this.wheel.wheelCy,
            radius,
          );
          x = pt.x;
          y = pt.y;
        } else if (!Number.isFinite(x) || !Number.isFinite(y)) {
          return;
        }
      }
    } else if (op.from === 'scale' || op.from === 'calculator_value') {
      const pt = E6bCalibrator.calculatorPointAtValue(
        Number(op.value),
        this.wheel.wheelCx,
        this.wheel.wheelCy,
        Number.isFinite(op.radius as number)
          ? Number(op.radius)
          : E6bCalibrator.calculatorScaleRadius,
      );
      x = pt.x;
      y = pt.y;
    } else if (op.from === 'grommet' || !Number.isFinite(x) || !Number.isFinite(y)) {
      const scale = mode.endsWith('high') ? 'high' : 'low';
      const pt = E6bCalibrator.windDotFromGrommet(
        Number(op.offset_kt),
        Number(op.drift_deg),
        this.wheel.wheelCx,
        this.wheel.wheelCy,
        scale,
        this.wheel.rotation,
        this.wheel.slideY,
      );
      x = pt.x;
      y = pt.y;
    }

    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const dirDeg = Number.isFinite(op.dir_deg)
      ? Number(op.dir_deg)
      : Number.isFinite(op.heading)
        ? Number(op.heading)
        : null;
    const kt = Number.isFinite(op.offset_kt) ? Math.abs(Number(op.offset_kt)) : null;
    // offset_kt is the length of the vector only while it runs straight up or
    // down the centreline. Give it a drift angle and the mark moves onto
    // another speed arc, so the distance from the grommet is the third side of
    // the triangle and the offset would label the dot with a speed it is not.
    const straight = !Number.isFinite(op.drift_deg) || Number(op.drift_deg) === 0;
    let label = op.label != null ? String(op.label).trim() : '';
    if (!label && dirDeg != null) {
      label = kt != null ? `${Math.round(dirDeg)}°/${Math.round(kt)}` : `${Math.round(dirDeg)}°`;
    } else if (!label && kt != null && straight) {
      label = String(Math.round(kt));
    } else if (!label && kt != null && !straight && Number.isFinite(op.offset_kt)) {
      // Off-centreline estimate mark (reverse wind-find): offset_kt here is a
      // delta from the grommet's current TAS, not the plotted value itself -
      // the reading is the speed the mark actually sits at.
      const estScale = mode.endsWith('high') ? 'high' : 'low';
      const tasNow = E6bCalibrator.tasAtCardY(
        E6bCalibrator.windGrommetY - this.wheel.slideY,
        estScale,
      );
      const markSpeed = tasNow + Number(op.offset_kt);
      if (Number.isFinite(markSpeed)) label = String(Math.round(markSpeed));
    }
    const marks = this.wheel.getMarks().slice();
    marks.push({
      kind: 'dot',
      x,
      y,
      ring,
      label: label || undefined,
    });
    this.wheel.setMarks(marks);
  }

  private applyLine(op: E6bOp): void {
    let x1 = Number(op.x1);
    let y1 = Number(op.y1);
    let x2 = Number(op.x2);
    let y2 = Number(op.y2);
    let ring: 'inner' | 'outer' = op.ring === 'inner' ? 'inner' : 'outer';
    if (
      String(this.wheel.mode) === 'calculator' &&
      (op.from === 'radial' || op.from === 'scale') &&
      Number.isFinite(Number(op.value))
    ) {
      const ang = E6bCalibrator.calculatorAngleOfValue(Number(op.value));
      const inner = E6bCalibrator.calculatorPointAtAngle(
        ang,
        this.wheel.wheelCx,
        this.wheel.wheelCy,
        E6bCalibrator.calculatorScaleRadius,
      );
      const outer = E6bCalibrator.calculatorPointAtAngle(
        ang,
        this.wheel.wheelCx,
        this.wheel.wheelCy,
        E6bCalibrator.calculatorOuterScaleRadius + 6,
      );
      x1 = inner.x;
      y1 = inner.y;
      x2 = outer.x;
      y2 = outer.y;
      ring = 'outer';
    }
    if (![x1, y1, x2, y2].every(Number.isFinite)) return;
    const marks = this.wheel.getMarks().slice();
    marks.push({ kind: 'line', x1, y1, x2, y2, ring });
    this.wheel.setMarks(marks);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.timer = setTimeout(() => {
        this.timer = null;
        resolve();
      }, ms);
    });
  }
}

export const E6B_TURN_MS = TURN_MS;
export const E6B_SLIDE_MS = SLIDE_MS;
